// Content cards: area cards, guide cards, closing-page cards and the
// loading / could-not-load / empty states. Every card is an instance of a
// Figma component on the design file's Components page.
import React, { useState } from 'react';
import { LevelRow, LevelChip, Mark } from './levels';
import { chevronRightTeal, chevronDownTeal, chevronUpTeal, iconNoConnection } from '../icons';
import { LEVELS } from '../hfpDisplay';

export function Card({ children, className = '', tinted = false, padded = true, desktop = false }) {
  return (
    <div
      className={`w-full rounded-[var(--hfp-radius-16)] border border-solid border-[color:var(--hfp-line-hair)] ${
        tinted ? 'bg-[var(--hfp-surface-head-plum)]' : 'bg-[var(--hfp-surface-card)]'
      } ${padded ? 'p-[16px]' : ''} ${className}`}
      style={{ boxShadow: desktop ? 'var(--hfp-shadow-card-desktop)' : 'var(--hfp-shadow-card)' }}
    >
      {children}
    </div>
  );
}

/** Teal text link in a 44px row. Chevron = pushes/opens; plain = raises a sheet. */
export function LinkRow({ label, onClick, chevron = false, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[44px] items-center gap-[8px] w-full rounded-[var(--hfp-radius-8)] text-left ${className}`}
    >
      <span className={`hfp-body-16 text-[color:var(--hfp-link-teal)] ${chevron ? 'hfp-sub-16 flex-1 min-w-0' : ''}`}>{label}</span>
      {chevron && <img src={chevronRightTeal} alt="" aria-hidden="true" width={20} height={20} />}
    </button>
  );
}

/**
 * One area: title · Home row · School row (never merged) · story · help links.
 * Help link rows appear only where that setting has written ideas.
 */
export function AreaCard({
  area,
  desktop = false,
  storyLoading = false,
  storyUnavailable = false,
  onHelp,
  onOpenMeaning,
}) {
  const levelTable = (
    <div className="flex flex-col gap-[2px] w-full overflow-hidden rounded-[var(--hfp-radius-12)]">
      <LevelRow
        setting="home"
        level={area.home}
        compact={desktop}
        onOpenMeaning={onOpenMeaning ? () => onOpenMeaning(area, 'home') : undefined}
      />
      <LevelRow
        setting="school"
        level={area.school}
        compact={desktop}
        onOpenMeaning={onOpenMeaning ? () => onOpenMeaning(area, 'school') : undefined}
      />
    </div>
  );

  const story = area.story ? (
    <p className="hfp-body-16 w-full max-w-[600px] text-[color:var(--hfp-ink-mid)]">{area.story}</p>
  ) : storyLoading ? (
    <div className="flex w-full max-w-[600px] flex-col gap-[8px]" aria-hidden="true">
      <div className="hfp-skeleton-bar h-[14px] w-full rounded-[var(--hfp-radius-4)] bg-[var(--hfp-line-hair)]" />
      <div className="hfp-skeleton-bar h-[14px] w-[70%] rounded-[var(--hfp-radius-4)] bg-[var(--hfp-line-hair)]" />
    </div>
  ) : storyUnavailable ? (
    // The levels above always stand; only the written summary failed.
    <p className="hfp-meta-14 w-full max-w-[600px] text-[color:var(--hfp-ink-faint)]">
      The written summary for this area is not available right now.
    </p>
  ) : null;

  if (desktop) {
    return (
      <Card desktop className="!p-[24px] !border-[color:var(--hfp-line-border)] flex flex-col gap-[16px]">
        <h2 className="hfp-heading-20 w-full">{area.name}</h2>
        <div className="flex items-start gap-[40px] w-full">
          <div className="w-[320px] shrink-0">{levelTable}</div>
          <div className="flex flex-1 min-w-0 flex-col gap-[8px]">{story}</div>
        </div>
      </Card>
    );
  }

  const helpLinks = [
    area.help.home && { setting: 'home', label: 'What could help at home' },
    area.help.school && { setting: 'school', label: 'What could help at school' },
  ].filter(Boolean);

  return (
    <Card className="flex flex-col gap-[16px]">
      <h2 className="hfp-heading-18 w-full">{area.name}</h2>
      {levelTable}
      {story}
      {helpLinks.length > 0 && (
        <div className="flex flex-col w-full">
          <div aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
          <div className="flex flex-col pt-[4px] w-full">
            {helpLinks.map((link) => (
              <LinkRow
                key={link.setting}
                label={link.label}
                chevron
                onClick={() => onHelp(area, link.setting)}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Guide cards (How to use it). Ratified copy, fixed per kind.
// ---------------------------------------------------------------------------
export function guideCards({ childName, showPdfLink }) {
  return [
    {
      number: 1,
      title: 'What is this report?',
      body: `It shows what comes easily to ${childName} and where support would help, at home and at school. It is not a diagnosis.`,
    },
    {
      number: 2,
      title: 'Who answered the questions?',
      body: 'Home and school answered the same questions separately. If answers differ, it is because home and school are different places, and neither is wrong.',
    },
    {
      number: 3,
      title: 'What do the four levels mean?',
      body: 'Every area has one level from home and one from school. Each is written in words, so you never need to remember a colour.',
      levelMap: true,
    },
    {
      number: 4,
      title: 'Where is everything?',
      body: 'Three tabs at the bottom of the screen: How to use it (this guide), The profile, and What could help. Tabs along the top take you between the pages inside each part.',
      bodyDesktop: 'There are three tabs at the top. Read this guide first, then “The profile”, then “What could help”.',
      tabMap: true,
    },
    {
      number: 5,
      title: 'What should we do next?',
      body: 'Read it before you talk together. Then agree two or three things to try first, who will do them, and when to check in.',
      pdfLink: showPdfLink,
    },
  ];
}

function GuideCardExtras({ card, desktop, onLevelsLink, onDownload }) {
  return (
    <>
      {card.levelMap && (
        <div className={`flex ${desktop ? 'flex-col gap-[8px]' : 'flex-wrap gap-[8px]'} items-start w-full`}>
          {['priority', 'support', 'well', 'none'].map((level) =>
            desktop ? (
              <span key={level} className="flex items-center gap-[14px] w-full">
                <LevelChip level={level} />
                <span className="hfp-meta-14 flex-1 min-w-0 text-[color:var(--hfp-ink-mid)]">{LEVELS[level].meaning}</span>
              </span>
            ) : (
              <LevelChip key={level} level={level} />
            )
          )}
        </div>
      )}
      {card.levelMap && desktop && (
        <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">
          Shapes help colour-blind readers tell the levels apart. Select any level in the report to see what it means and what could help.
        </p>
      )}
      {card.levelMap && !desktop && (
        <LinkRow label="What the levels mean" onClick={onLevelsLink} />
      )}
      {card.tabMap && (
        <div className="flex flex-wrap gap-[8px] items-start w-full">
          {/* Desktop shows the three-part map with the current part filled;
              mobile shows the two other parts as tinted chips. */}
          {(desktop ? ['How to use it', 'The profile', 'What could help'] : ['The profile', 'What could help']).map((label, i) => {
            const filled = desktop && i === 0;
            return (
              <span
                key={label}
                className={`flex px-[12px] py-[6px] rounded-[var(--hfp-radius-8)] ${filled ? 'bg-[var(--hfp-ink-heading)]' : 'bg-[var(--hfp-surface-head-plum)]'}`}
              >
                <span className={`hfp-label-14 whitespace-nowrap ${filled ? 'text-[color:var(--hfp-ink-on-plum)]' : 'text-[color:var(--hfp-ink-heading)]'}`}>
                  {label}
                </span>
              </span>
            );
          })}
        </div>
      )}
      {card.pdfLink && (
        <LinkRow label="Download the PDF to take to that conversation." onClick={onDownload} />
      )}
    </>
  );
}

/** Mobile guide card: always open. */
export function MobileGuideCard({ card, onLevelsLink, onDownload }) {
  return (
    <Card className="!border-[color:var(--hfp-line-border)] flex flex-col gap-[12px]" >
      <div className="flex items-start gap-[10px] w-full">
        <span className="flex items-center justify-center size-[28px] shrink-0 rounded-[var(--hfp-radius-8)] bg-[var(--hfp-ink-heading)]">
          <span className="hfp-label-14 text-[color:var(--hfp-ink-on-plum)]">{card.number}</span>
        </span>
        <div className="flex flex-1 min-w-0 flex-col gap-[4px]">
          <h2 className="hfp-heading-18 w-full">{card.title}</h2>
          <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{card.body}</p>
          <GuideCardExtras card={card} desktop={false} onLevelsLink={onLevelsLink} onDownload={onDownload} />
        </div>
      </div>
    </Card>
  );
}

/** Desktop guide card: accordion, the whole head is the toggle. */
export function DesktopGuideCard({ card, open, onToggle, onDownload }) {
  return (
    <Card className="!px-[24px] !py-[18px] !border-[color:var(--hfp-line-border)] flex flex-col gap-[12px]">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-start gap-[14px] w-full text-left">
        <span className="flex items-center justify-center size-[28px] shrink-0 rounded-[var(--hfp-radius-8)] bg-[var(--hfp-ink-heading)]">
          <span className="hfp-label-14 text-[color:var(--hfp-ink-on-plum)]">{card.number}</span>
        </span>
        <span className="flex flex-1 min-w-0 flex-col gap-[4px]">
          <span className="hfp-heading-18 w-full">{card.title}</span>
          <span className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{card.bodyDesktop || card.body}</span>
        </span>
        <span className="flex items-center gap-[6px] shrink-0">
          <span className="hfp-label-14 text-[color:var(--hfp-link-teal)] whitespace-nowrap">{open ? 'Show less' : 'Show more'}</span>
          <img src={open ? chevronUpTeal : chevronDownTeal} alt="" aria-hidden="true" width={16} height={16} />
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-[8px] w-full pl-[42px]">
          <GuideCardExtras card={card} desktop onDownload={onDownload} />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Closing page (mobile only)
// ---------------------------------------------------------------------------
export function BulletCard({ title, bullets }) {
  return (
    <Card className="flex flex-col gap-[12px]">
      <h2 className="hfp-heading-18">{title}</h2>
      {bullets.map((text) => (
        <div key={text} className="flex items-start gap-[8px] w-full">
          <span className="hfp-body-16 w-[12px] text-center shrink-0 text-[color:var(--hfp-ink-strong)]">•</span>
          <p className="hfp-body-16 flex-1 min-w-0 text-[color:var(--hfp-ink-strong)]">{text}</p>
        </div>
      ))}
    </Card>
  );
}

export function TextCard({ title, body }) {
  return (
    <Card tinted className="flex flex-col gap-[8px]">
      <h2 className="hfp-heading-18 whitespace-nowrap">{title}</h2>
      <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-strong)]">{body}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/** Loading placeholder in the shape of an area card. Grey bars, aria-hidden. */
export function SkeletonCard() {
  return (
    <div
      aria-hidden="true"
      className="flex w-full flex-col gap-[12px] p-[16px] rounded-[var(--hfp-radius-16)] border border-solid border-[color:var(--hfp-line-hair)] bg-[var(--hfp-surface-card)]"
    >
      <div className="hfp-skeleton-bar h-[20px] w-[170px] rounded-[var(--hfp-radius-8)] bg-[var(--hfp-line-border)]" />
      <div className="h-[76px] w-full" />
      <div className="hfp-skeleton-bar h-[14px] w-full rounded-[var(--hfp-radius-4)] bg-[var(--hfp-line-hair)]" />
      <div className="hfp-skeleton-bar h-[14px] w-full rounded-[var(--hfp-radius-4)] bg-[var(--hfp-line-hair)]" />
      <div className="hfp-skeleton-bar h-[14px] w-[210px] rounded-[var(--hfp-radius-4)] bg-[var(--hfp-line-hair)]" />
    </div>
  );
}

/** Could-not-load state: icon, title, body and the outline Try again. */
export function ErrorCard({ title, body, onRetry }) {
  return (
    <div className="flex w-full flex-col items-center gap-[12px] px-[20px] pt-[24px] pb-[20px] rounded-[var(--hfp-radius-16)] bg-[var(--hfp-surface-card)]">
      <span className="flex items-center justify-center size-[56px] rounded-[var(--hfp-radius-pill)] bg-[var(--hfp-surface-head-plum)]">
        <img src={iconNoConnection} alt="" aria-hidden="true" width={32} height={32} />
      </span>
      <h2 className="hfp-heading-18 w-full text-center">{title}</h2>
      <p className="hfp-body-16 w-full text-center text-[color:var(--hfp-ink-mid)]">{body}</p>
      <button
        type="button"
        onClick={onRetry}
        className="flex h-[48px] w-full items-center justify-center rounded-[var(--hfp-radius-12)] border-2 border-solid border-[color:var(--hfp-ink-heading)]"
      >
        <span className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">Try again</span>
      </button>
    </div>
  );
}

/**
 * Pain safety note on the Sensory section (INSTRUCTION-family-report-narrative,
 * 2026-07-22): NOT model-generated — rendered verbatim from the pain override
 * flag, because it is the one sentence with a safety consequence if
 * paraphrased, softened or dropped.
 */
export function SafetyNoteCard({ childName }) {
  return (
    <div className="flex items-start gap-[12px] w-full px-[16px] py-[14px] rounded-[var(--hfp-radius-16)] border border-solid border-[color:var(--hfp-safety-line)] bg-[var(--hfp-safety-bg)]">
      <Mark level="priority" size={18} />
      <p className="hfp-body-16 flex-1 min-w-0 text-[color:var(--hfp-safety-ink)]">
        <strong>Safety note:</strong> {childName} may not notice pain the way other children do;
        worth everyone knowing, so injuries and illness are not missed.
      </p>
    </div>
  );
}

/** Shown when a help list has nothing to show for a setting. No control. */
export function EmptyState({ title, body }) {
  return (
    <div className="flex w-full flex-col gap-[8px] px-[16px] py-[20px] rounded-[var(--hfp-radius-16)] border border-solid border-[color:var(--hfp-line-hair)] bg-[var(--hfp-surface-card)]">
      <h2 className="hfp-heading-18 w-full">{title}</h2>
      <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{body}</p>
    </div>
  );
}
